import { storage, db, auth } from "./firebase"

import { 
  ref, 
  uploadString, 
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL, 
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata
} from "firebase/storage";

import {
  collection,
  doc,
  deleteDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";

class FirestoreService {
  // Wait for authentication to complete
  waitForAuth = async () => {
    return new Promise((resolve, reject) => {
      // If already authenticated, return immediately
      if (auth.currentUser) {
        // console.log('✅ Firebase: User already authenticated:', auth.currentUser.email);
        resolve(auth.currentUser);
        return;
      }
      
      // Wait for authentication state change
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        if (user) {
          // console.log('✅ Firebase: Authentication completed:', user.email);
          resolve(user);
        } else {
          console.error('❌ Firebase: No authenticated user found');
          reject(new Error('User not authenticated'));
        }
      });
      
      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        reject(new Error('Authentication timeout'));
      }, 10000);
    });
  }

  fetchCollectionData = (collectionName, callback) => {
    const data = []
    onSnapshot(collection(db, collectionName), (querySnapshot) => {
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() })
      })
      callback(data)
    })
  }

  fetchCollectionDataAsync = async (collectionName) => {
    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      const data = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() });
      });
      return data;
    } catch (error) {
      console.error('Error fetching collection data:', error);
      throw error;
    }
  }

  getData = async (collectionName, docID) => {
    try {
      const docRef = doc(db, collectionName, docID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.error("No such document!");
        return null;
      }
      
      return docSnap.data();
    } catch (error) {
      console.error("Error fetching document:", error);
      throw error;
    }
  }

  deleteDoc = async (collection, docID) => {
    await deleteDoc(doc(db, collection, docID))
  }

  updateDoc = async (collection, docID, data) => {
    await updateDoc(doc(db, collection, docID), data)
  }

  addDoc = async (collection, docID, data) => {
    await setDoc(doc(db, collection, docID), data)
  }

  // Storage Methods
  getFileUrl = async (fileName) => {
    const imageRef = ref(storage, fileName);
    try {
      // console.log(`🔗 Firebase: Getting file URL for ${fileName}`);
      const url = await getDownloadURL(imageRef);
      // console.log(`✅ Firebase: Successfully got URL for ${fileName}`);
      return url;
    } catch (error) {
      if (error?.code !== 'storage/object-not-found') {
        console.error(`❌ Firebase: Error getting URL for ${fileName}:`, error);
      }
      return null;
    }
  }

  // Upload file from File object
  uploadFile = async (file, path, metadata = {}) => {
    try {
      // console.log(`🚀 Firebase: Starting upload process for ${file.name}`);
      // console.log(`📁 Firebase: File size: ${file.size} bytes`);
      // console.log(`📁 Firebase: File type: ${file.type}`);
      // console.log(`📁 Firebase: Target path: ${path}`);
      
      // Wait for authentication to complete for uploads
      const user = await this.waitForAuth();
      // console.log(`✅ Firebase: Authentication confirmed for user: ${user.email}`);
      // console.log(`📤 Firebase: Uploading file to ${path} (User: ${user.email})`);
      
      const storageRef = ref(storage, path);
      // console.log(`🔗 Firebase: Storage reference created for path: ${path}`);
      
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      // console.log(`📤 Firebase: Upload task created, starting upload...`);
      
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Progress tracking
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          },
          (error) => {
            console.error('❌ Firebase: Upload failed with error:', error);
            console.error('❌ Firebase: Error code:', error.code);
            console.error('❌ Firebase: Error message:', error.message);
            console.error('❌ Firebase: Full error object:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                path: path,
                metadata: uploadTask.snapshot.metadata
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Upload file from base64 string
  uploadBase64File = async (base64String, path, metadata = {}) => {
    try {
      const storageRef = ref(storage, path);
      const uploadTask = await uploadString(storageRef, base64String, 'data_url', metadata);
      const downloadURL = await getDownloadURL(uploadTask.ref);
      
      return {
        url: downloadURL,
        path: path,
        metadata: uploadTask.metadata
      };
    } catch (error) {
      console.error('Error uploading base64 file:', error);
      throw error;
    }
  }

  // Upload file from ArrayBuffer
  uploadBytes = async (bytes, path, metadata = {}) => {
    try {
      const storageRef = ref(storage, path);
      const uploadTask = await uploadBytes(storageRef, bytes, metadata);
      const downloadURL = await getDownloadURL(uploadTask.ref);
      
      return {
        url: downloadURL,
        path: path,
        metadata: uploadTask.metadata
      };
    } catch (error) {
      console.error('Error uploading bytes:', error);
      throw error;
    }
  }

  // Delete file
  deleteFile = async (path) => {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // List files in a directory
  listFiles = async (path) => {
    try {
      const storageRef = ref(storage, path);
      const result = await listAll(storageRef);
      
      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const metadata = await getMetadata(itemRef);
          const url = await getDownloadURL(itemRef);
          return {
            name: itemRef.name,
            path: itemRef.fullPath,
            url: url,
            metadata: metadata
          };
        })
      );
      
      return files;
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  }

  // List only file names in a directory (lightweight, no metadata/URL fetching)
  listFileNames = async (path) => {
    try {
      const storageRef = ref(storage, path);
      const result = await listAll(storageRef);
      return result.items.map(item => item.name);
    } catch (error) {
      console.error('Error listing file names:', error);
      return [];
    }
  }

  // Get file metadata
  getFileMetadata = async (path) => {
    try {
      const storageRef = ref(storage, path);
      const metadata = await getMetadata(storageRef);
      return metadata;
    } catch (error) {
      console.error('Error getting file metadata:', error);
      throw error;
    }
  }

  // Update file metadata
  updateFileMetadata = async (path, newMetadata) => {
    try {
      const storageRef = ref(storage, path);
      await updateMetadata(storageRef, newMetadata);
      return true;
    } catch (error) {
      console.error('Error updating file metadata:', error);
      throw error;
    }
  }

  // Book-specific storage methods
  uploadBookImage = async (file, bookId, imageName) => {
    const path = `books/${bookId}/images/${imageName}`;
    const metadata = {
      contentType: file.type,
      customMetadata: {
        bookId: bookId,
        type: 'book-image'
      }
    };
    return await this.uploadFile(file, path, metadata);
  }

  uploadProjectLogo = async (file, projectId) => {
    const path = `projects/${projectId}/logo.webp`;
    const metadata = {
      contentType: file.type,
      customMetadata: {
        projectId: projectId,
        type: 'project-logo'
      }
    };
    await this.uploadFile(file, path, metadata);
    const url = await getDownloadURL(ref(storage, path));
    await updateDoc(doc(db, 'projects', projectId), { logoUrl: url });
    return url;
  }

  uploadCompanyLogo = async (file, companyId, logoName) => {
    const path = `companies/${companyId}/logos/${logoName}`;
    const metadata = {
      contentType: file.type,
      customMetadata: {
        companyId: companyId,
        type: 'company-logo'
      }
    };
    return await this.uploadFile(file, path, metadata);
  }

  // Get book images
  getBookImages = async (bookId) => {
    const path = `books/${bookId}/images`;
    return await this.listFiles(path);
  }

  // Get company logos
  getCompanyLogos = async (companyId) => {
    const path = `companies/${companyId}/logos`;
    return await this.listFiles(path);
  }
}

const firebase = new FirestoreService()
export default firebase
